import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  Filter,
  SlidersHorizontal,
  CheckCircle,
  Circle,
  Calendar,
  User,
  Tag,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Loader2,
  Grid3x3,
  List,
  CheckSquare,
  Paperclip,
  MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import taskService from '@/services/taskService';
import TaskCardModal from './TaskCardModal';

const TaskListView = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Estados principais
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [boards, setBoards] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBoard, setSelectedBoard] = useState('all');
  const [selectedColumn, setSelectedColumn] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedAssignee, setSelectedAssignee] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showOverdue, setShowOverdue] = useState(false);
  
  // Estados de ordenação
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Estados do modal
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  
  // Colunas disponíveis do board selecionado
  const [availableColumns, setAvailableColumns] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedBoard !== 'all') {
      loadBoardColumns(selectedBoard);
    } else {
      setAvailableColumns([]);
      setSelectedColumn('all');
    }
  }, [selectedBoard]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [cardsData, boardsData, usersData] = await Promise.all([
        taskService.getAllCards(),
        taskService.getBoards({}),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/usuarios`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.ok ? r.json() : []).catch(() => [])
      ]);

      setCards(cardsData || []);
      setBoards(boardsData || []);
      setUsers(usersData.filter(u => u.role === 'tecnico' || u.role === 'admin') || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: 'Erro ao carregar dados',
        description: 'Não foi possível carregar as tarefas.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBoardColumns = async (boardId) => {
    try {
      const boardData = await taskService.getBoardById(boardId);
      setAvailableColumns(boardData.columns || []);
    } catch (error) {
      console.error('Erro ao carregar colunas:', error);
    }
  };

  const handleCardUpdated = async (updatedCard) => {
    if (updatedCard) {
      setCards(prevCards => prevCards.map(c => c._id === updatedCard._id ? updatedCard : c));
    } else {
      await loadInitialData();
    }
  };

  const handleOpenCard = (card) => {
    setSelectedCard(card);
    setShowCardModal(true);
  };

  // Filtrar e ordenar cards
  const getFilteredAndSortedCards = () => {
    let filtered = [...cards];

    // Filtro de busca
    if (searchTerm) {
      filtered = filtered.filter(card =>
        card.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro de board
    if (selectedBoard !== 'all') {
      filtered = filtered.filter(card => card.board_id === selectedBoard);
    }

    // Filtro de coluna
    if (selectedColumn !== 'all') {
      filtered = filtered.filter(card => card.column_id === selectedColumn);
    }

    // Filtro de prioridade
    if (selectedPriority !== 'all') {
      filtered = filtered.filter(card => card.priority === selectedPriority);
    }

    // Filtro de assignee
    if (selectedAssignee !== 'all') {
      filtered = filtered.filter(card => card.assigned_to === selectedAssignee);
    }

    // Filtro de status
    if (selectedStatus !== 'all') {
      const isCompleted = selectedStatus === 'completed';
      filtered = filtered.filter(card => card.is_completed === isCompleted);
    }

    // Filtro de vencidas
    if (showOverdue) {
      filtered = filtered.filter(card => 
        card.due_date && !card.is_completed && taskService.isOverdue(card.due_date, card.is_completed)
      );
    }

    // Ordenação
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Tratamento especial para datas
      if (sortBy === 'due_date' || sortBy === 'created_at') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Tratamento especial para strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || '';
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const filteredCards = getFilteredAndSortedCards();

  const getPriorityBadgeClass = (priority) => {
    const classes = {
      baixa: 'bg-gray-100 text-gray-800 border-gray-300',
      media: 'bg-blue-100 text-blue-800 border-blue-300',
      alta: 'bg-orange-100 text-orange-800 border-orange-300',
      urgente: 'bg-red-100 text-red-800 border-red-300'
    };
    return classes[priority] || classes.media;
  };

  const getColumnName = (columnId) => {
    if (selectedBoard !== 'all') {
      const column = availableColumns.find(col => col._id === columnId);
      return column?.title || 'Sem coluna';
    }
    
    // Se não há board selecionado, buscar em todos os boards
    for (const board of boards) {
      const column = board.columns?.find(col => col._id === columnId);
      if (column) return column.title;
    }
    return 'Sem coluna';
  };

  const getBoardName = (boardId) => {
    const board = boards.find(b => b._id === boardId);
    return board?.title || 'Sem board';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u._id === userId);
    return user?.name || 'Não atribuído';
  };

  const calculateChecklistProgress = (card) => {
    if (!card.checklists || card.checklists.length === 0) {
      return { total: 0, completed: 0, percentage: 0 };
    }
    
    const total = card.checklists.length;
    const completed = card.checklists.filter(item => item.is_completed).length;
    const percentage = Math.round((completed / total) * 100);
    
    return { total, completed, percentage };
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBoard('all');
    setSelectedColumn('all');
    setSelectedPriority('all');
    setSelectedAssignee('all');
    setSelectedStatus('all');
    setShowOverdue(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/portal/tasks')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Lista de Tarefas</h1>
            <p className="text-sm text-gray-500">
              {filteredCards.length} {filteredCards.length === 1 ? 'tarefa' : 'tarefas'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/portal/tasks')}
            className="gap-2"
          >
            <Grid3x3 className="h-4 w-4" />
            Kanban
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Primeira linha de filtros */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Busca */}
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar tarefas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Board */}
              <Select value={selectedBoard} onValueChange={setSelectedBoard}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os boards" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os boards</SelectItem>
                  {boards.map(board => (
                    <SelectItem key={board._id} value={board._id}>
                      {board.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Coluna */}
              <Select 
                value={selectedColumn} 
                onValueChange={setSelectedColumn}
                disabled={selectedBoard === 'all'}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas as colunas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as colunas</SelectItem>
                  {availableColumns.map(column => (
                    <SelectItem key={column._id} value={column._id}>
                      {column.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Segunda linha de filtros */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Prioridade */}
              <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                <SelectTrigger>
                  <SelectValue placeholder="Prioridade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas prioridades</SelectItem>
                  <SelectItem value="baixa">Baixa</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>

              {/* Assignee */}
              <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                <SelectTrigger>
                  <SelectValue placeholder="Responsável" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user._id} value={user._id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status */}
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos status</SelectItem>
                  <SelectItem value="active">Em andamento</SelectItem>
                  <SelectItem value="completed">Concluídas</SelectItem>
                </SelectContent>
              </Select>

              {/* Ordenação */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Data de criação</SelectItem>
                  <SelectItem value="due_date">Data de vencimento</SelectItem>
                  <SelectItem value="priority">Prioridade</SelectItem>
                  <SelectItem value="title">Título</SelectItem>
                </SelectContent>
              </Select>

              {/* Botões de ação */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="flex-1"
                >
                  {sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="flex-1"
                >
                  Limpar
                </Button>
              </div>
            </div>

            {/* Filtros adicionais */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOverdue}
                  onChange={(e) => setShowOverdue(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-gray-700">Apenas vencidas</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Cards */}
      <div className="space-y-2">
        {filteredCards.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-gray-500">
                <List className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Nenhuma tarefa encontrada</p>
                <p className="text-sm mt-2">Tente ajustar os filtros ou criar uma nova tarefa</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredCards.map((card) => {
            const checklistProgress = calculateChecklistProgress(card);
            const isOverdue = taskService.isOverdue(card.due_date, card.is_completed);
            const daysUntilDue = taskService.daysUntilDue(card.due_date);

            return (
              <Card
                key={card._id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleOpenCard(card)}
              >
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    {/* Status checkbox */}
                    <div className="mt-1">
                      {card.is_completed ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <Circle className="h-5 w-5 text-gray-400" />
                      )}
                    </div>

                    {/* Conteúdo principal */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* Título */}
                          <h3 className={`font-medium text-gray-900 ${card.is_completed ? 'line-through text-gray-500' : ''}`}>
                            {card.title}
                          </h3>

                          {/* Metadados */}
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                            {/* Board e Coluna */}
                            <span className="flex items-center gap-1">
                              <Grid3x3 className="h-3 w-3" />
                              {getBoardName(card.board_id)} / {getColumnName(card.column_id)}
                            </span>

                            {/* Assignee */}
                            {card.assigned_to && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {getUserName(card.assigned_to)}
                              </span>
                            )}

                            {/* Data de vencimento */}
                            {card.due_date && (
                              <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                                <Calendar className="h-3 w-3" />
                                {taskService.formatDueDate(card.due_date)}
                                {isOverdue && <AlertCircle className="h-3 w-3" />}
                              </span>
                            )}

                            {/* Checklist */}
                            {checklistProgress.total > 0 && (
                              <span className="flex items-center gap-1">
                                <CheckSquare className="h-3 w-3" />
                                {checklistProgress.completed}/{checklistProgress.total}
                              </span>
                            )}

                            {/* Anexos */}
                            {card.attachments && card.attachments.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Paperclip className="h-3 w-3" />
                                {card.attachments.length}
                              </span>
                            )}

                            {/* Comentários */}
                            {card.comments && card.comments.length > 0 && (
                              <span className="flex items-center gap-1">
                                <MessageSquare className="h-3 w-3" />
                                {card.comments.length}
                              </span>
                            )}
                          </div>

                          {/* Labels */}
                          {card.labels && card.labels.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {card.labels.map((label, idx) => (
                                <Badge
                                  key={idx}
                                  variant="outline"
                                  className="text-xs"
                                >
                                  {label}
                                </Badge>
                              ))}
                            </div>
                          )}

                          {/* Progress bar do checklist */}
                          {checklistProgress.total > 0 && (
                            <div className="mt-2">
                              <Progress value={checklistProgress.percentage} className="h-1" />
                            </div>
                          )}
                        </div>

                        {/* Badge de prioridade */}
                        <Badge
                          variant="outline"
                          className={`${getPriorityBadgeClass(card.priority)} text-xs`}
                        >
                          {taskService.translatePriority(card.priority)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Card Modal */}
      <TaskCardModal
        isOpen={showCardModal}
        onClose={() => {
          setShowCardModal(false);
          setSelectedCard(null);
        }}
        card={selectedCard}
        boardId={selectedCard?.board_id}
        onCardUpdated={handleCardUpdated}
      />
    </div>
  );
};

export default TaskListView;
